// frontend/src/components/Dashboard/PaymentMethods.jsx
import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCreditCard,
  faMobileAlt,
  faTrash,
  faPlus,
  faTimes,
  faCheckCircle,
  faSpinner,
  faExclamationTriangle,
  faWallet,
  faMoneyBillWave,
  faBuilding,
  faPhone,
  faCalendarAlt,
  faUser,
  faLock,
} from "@fortawesome/free-solid-svg-icons";
import {
  getPaymentMethods,
  addPaymentMethod,
  removePaymentMethod,
  setDefaultPaymentMethod,
} from "../../api";
import { useCurrency } from "../../context/CurrencyContext";

const PaymentMethods = ({ onRefresh }) => {
  const { formatPrice } = useCurrency();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Form state
  const [formData, setFormData] = useState({
    type: "mobile_money",
    phoneNumber: "",
    cardNumber: "",
    expiryDate: "",
    cvv: "",
    nameOnCard: "",
    isDefault: false,
  });

  // Sierra Leone mobile money providers
  const mobileMoneyProviders = [
    { value: "mobile_money", label: "Mobile Money" },
    { value: "orange_money", label: "Orange Money" },
    { value: "afrimoney", label: "Afrimoney" },
    { value: "wave", label: "WAVE" },
  ];

  const paymentTypes = [
    { value: "mobile_money", label: "Mobile Money", icon: faMobileAlt },
    { value: "card", label: "Credit/Debit Card", icon: faCreditCard },
  ];

  // Fetch payment methods
  const fetchPaymentMethods = async () => {
    setLoading(true);
    try {
      const response = await getPaymentMethods();
      setPaymentMethods(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching payment methods:", error);
      setError("Failed to load payment methods");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  // Handle form change
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setError("");
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    setSuccess("");

    // Validate based on type
    if (formData.type === "card") {
      if (
        !formData.cardNumber ||
        !formData.expiryDate ||
        !formData.cvv ||
        !formData.nameOnCard
      ) {
        setError("All card fields are required");
        setSubmitting(false);
        return;
      }
      // Basic card validation
      const cleanCard = formData.cardNumber.replace(/\s/g, "");
      if (!/^\d{15,16}$/.test(cleanCard)) {
        setError("Please enter a valid card number (15-16 digits)");
        setSubmitting(false);
        return;
      }
    } else {
      if (!formData.phoneNumber) {
        setError("Phone number is required for mobile money");
        setSubmitting(false);
        return;
      }
      // Sierra Leone phone validation
      const cleanPhone = formData.phoneNumber.replace(/\s/g, "");
      if (!/^(\+232|0)[7-8]\d{7}$/.test(cleanPhone)) {
        setError(
          "Please enter a valid Sierra Leone phone number (e.g., +23276123456 or 076123456)",
        );
        setSubmitting(false);
        return;
      }
    }

    try {
      const dataToSend = {
        type: formData.type,
        isDefault: formData.isDefault,
      };

      if (formData.type === "card") {
        dataToSend.cardNumber = formData.cardNumber.replace(/\s/g, "");
        dataToSend.expiryDate = formData.expiryDate;
        dataToSend.cvv = formData.cvv;
        dataToSend.nameOnCard = formData.nameOnCard;
      } else {
        dataToSend.phoneNumber = formData.phoneNumber;
      }

      await addPaymentMethod(dataToSend);

      setSuccess("Payment method added successfully!");
      setFormData({
        type: "mobile_money",
        phoneNumber: "",
        cardNumber: "",
        expiryDate: "",
        cvv: "",
        nameOnCard: "",
        isDefault: false,
      });

      await fetchPaymentMethods();
      if (onRefresh) onRefresh();

      setTimeout(() => setShowModal(false), 1500);
    } catch (error) {
      console.error("Error adding payment method:", error);
      setError(error.response?.data?.message || "Failed to add payment method");
    } finally {
      setSubmitting(false);
    }
  };

  // Handle remove payment method
  const handleRemove = async (id) => {
    if (!window.confirm("Are you sure you want to remove this payment method?"))
      return;

    try {
      await removePaymentMethod(id);
      await fetchPaymentMethods();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error("Error removing payment method:", error);
      setError("Failed to remove payment method");
    }
  };

  // Handle set default
  const handleSetDefault = async (id) => {
    try {
      await setDefaultPaymentMethod(id);
      await fetchPaymentMethods();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error("Error setting default payment method:", error);
      setError("Failed to set default payment method");
    }
  };

  // Format phone number display
  const formatPhone = (phone) => {
    if (!phone) return "";
    if (phone.startsWith("+232")) {
      return phone.replace("+232", "0");
    }
    return phone;
  };

  // Get provider label
  const getProviderLabel = (type) => {
    const provider = mobileMoneyProviders.find((p) => p.value === type);
    return provider?.label || type.replace("_", " ").toUpperCase();
  };

  if (loading) {
    return (
      <div className="payment-loading">
        <FontAwesomeIcon icon={faSpinner} spin />
        <span>Loading payment methods...</span>
      </div>
    );
  }

  return (
    <div className="payment-methods-container">
      {/* Header */}
      <div className="payment-header">
        <h3>
          <FontAwesomeIcon icon={faWallet} /> Payment Methods
        </h3>
        <button className="btn-primary" onClick={() => setShowModal(true)}>
          <FontAwesomeIcon icon={faPlus} /> Add Payment Method
        </button>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="payment-alert error">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span>{error}</span>
          <button onClick={() => setError("")}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}
      {success && (
        <div className="payment-alert success">
          <FontAwesomeIcon icon={faCheckCircle} />
          <span>{success}</span>
          <button onClick={() => setSuccess("")}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}

      {/* Payment Methods Grid */}
      <div className="payment-methods-grid">
        {paymentMethods.length === 0 ? (
          <div className="no-payment-methods">
            <FontAwesomeIcon icon={faWallet} size="2x" />
            <p>No payment methods added yet.</p>
            <p className="sub-text">
              Add a payment method to start making payments.
            </p>
          </div>
        ) : (
          paymentMethods.map((method) => (
            <div
              key={method._id}
              className={`payment-method-card ${method.isDefault ? "default" : ""}`}
            >
              <div className="payment-method-icon">
                <FontAwesomeIcon
                  icon={method.type === "card" ? faCreditCard : faMobileAlt}
                />
              </div>
              <div className="payment-method-details">
                <div className="payment-method-type">
                  {method.type === "card"
                    ? "Credit/Debit Card"
                    : getProviderLabel(method.type)}
                  {method.isDefault && (
                    <span className="default-badge">Default</span>
                  )}
                </div>
                <div className="payment-method-info">
                  {method.type === "card"
                    ? `**** **** **** ${method.last4}`
                    : formatPhone(method.phoneNumber)}
                  {method.type === "card" && method.nameOnCard && (
                    <span className="card-name">{method.nameOnCard}</span>
                  )}
                </div>
                <div className="payment-method-actions">
                  {!method.isDefault && (
                    <button
                      className="set-default-btn"
                      onClick={() => handleSetDefault(method._id)}
                    >
                      Set as Default
                    </button>
                  )}
                  <button
                    className="remove-btn"
                    onClick={() => handleRemove(method._id)}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add Payment Method Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <FontAwesomeIcon icon={faPlus} /> Add Payment Method
              </h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="payment-form">
              {/* Payment Type Selection */}
              <div className="form-group">
                <label>Payment Type *</label>
                <div className="payment-type-selector">
                  {paymentTypes.map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      className={`type-btn ${formData.type === type.value ? "active" : ""}`}
                      onClick={() =>
                        setFormData((prev) => ({ ...prev, type: type.value }))
                      }
                    >
                      <FontAwesomeIcon icon={type.icon} />
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Mobile Money Fields */}
              {formData.type !== "card" && (
                <div className="form-group">
                  <label>
                    <FontAwesomeIcon icon={faPhone} /> Phone Number *
                    <small className="helper-text">
                      Sierra Leone format: +23276123456 or 076123456
                    </small>
                  </label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    placeholder="+232 76 123456"
                    required
                  />
                </div>
              )}

              {/* Card Fields */}
              {formData.type === "card" && (
                <>
                  <div className="form-group">
                    <label>
                      <FontAwesomeIcon icon={faCreditCard} /> Card Number *
                    </label>
                    <input
                      type="text"
                      name="cardNumber"
                      value={formData.cardNumber}
                      onChange={handleChange}
                      placeholder="1234 5678 9012 3456"
                      maxLength="19"
                      required
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>
                        <FontAwesomeIcon icon={faCalendarAlt} /> Expiry Date *
                      </label>
                      <input
                        type="text"
                        name="expiryDate"
                        value={formData.expiryDate}
                        onChange={handleChange}
                        placeholder="MM/YY"
                        maxLength="5"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>
                        <FontAwesomeIcon icon={faLock} /> CVV *
                      </label>
                      <input
                        type="password"
                        name="cvv"
                        value={formData.cvv}
                        onChange={handleChange}
                        placeholder="123"
                        maxLength="4"
                        required
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>
                      <FontAwesomeIcon icon={faUser} /> Name on Card *
                    </label>
                    <input
                      type="text"
                      name="nameOnCard"
                      value={formData.nameOnCard}
                      onChange={handleChange}
                      placeholder="John Doe"
                      required
                    />
                  </div>
                </>
              )}

              {/* Set as Default */}
              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="isDefault"
                    checked={formData.isDefault}
                    onChange={handleChange}
                  />
                  Set as default payment method
                </label>
              </div>

              {error && (
                <div className="form-error">
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  <span>{error}</span>
                </div>
              )}

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin /> Adding...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faPlus} /> Add Payment Method
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .payment-methods-container {
          padding: 0.5rem 0;
        }

        .payment-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
          flex-wrap: wrap;
          gap: 0.75rem;
        }

        .payment-header h3 {
          color: #006B2E;
          font-size: 1.1rem;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .payment-header h3 svg {
          color: #D9A321;
        }

        .payment-loading {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 2rem;
          justify-content: center;
          color: #5A5A5A;
        }

        .payment-loading svg {
          color: #D9A321;
        }

        .payment-alert {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem 1rem;
          border-radius: 8px;
          margin-bottom: 1rem;
          position: relative;
        }

        .payment-alert.error {
          background: #fee2e2;
          color: #dc2626;
          border-left: 4px solid #dc2626;
        }

        .payment-alert.success {
          background: #dcfce7;
          color: #16a34a;
          border-left: 4px solid #16a34a;
        }

        .payment-alert button {
          background: none;
          border: none;
          cursor: pointer;
          color: inherit;
          margin-left: auto;
          opacity: 0.7;
          padding: 0.25rem;
        }

        .payment-alert button:hover {
          opacity: 1;
        }

        .payment-methods-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 1rem;
        }

        .no-payment-methods {
          grid-column: 1 / -1;
          text-align: center;
          padding: 3rem 1.5rem;
          color: #5A5A5A;
          background: #F8F8F8;
          border-radius: 12px;
          border: 2px dashed #e5e5e5;
        }

        .no-payment-methods svg {
          color: #D9A321;
          margin-bottom: 0.75rem;
        }

        .no-payment-methods p {
          margin: 0.25rem 0;
        }

        .no-payment-methods .sub-text {
          font-size: 0.75rem;
          color: #9ca3af;
        }

        .payment-method-card {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          padding: 1rem;
          background: #ffffff;
          border: 1px solid #e5e5e5;
          border-radius: 12px;
          transition: all 0.3s ease;
          position: relative;
        }

        .payment-method-card:hover {
          box-shadow: 0 4px 16px rgba(0,0,0,0.08);
          border-color: #D9A321;
        }

        .payment-method-card.default {
          border-color: #D9A321;
          background: #fef3c7;
        }

        .payment-method-card.default::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: linear-gradient(90deg, #D9A321, #B8860B);
          border-radius: 12px 12px 0 0;
        }

        .payment-method-icon {
          width: 44px;
          height: 44px;
          min-width: 44px;
          background: #F8F8F8;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #006B2E;
          font-size: 1.1rem;
        }

        .payment-method-details {
          flex: 1;
          min-width: 0;
        }

        .payment-method-type {
          font-weight: 600;
          font-size: 0.875rem;
          color: #1F1F1F;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .default-badge {
          font-size: 0.55rem;
          background: #D9A321;
          color: #ffffff;
          padding: 0.1rem 0.4rem;
          border-radius: 10px;
          font-weight: 600;
        }

        .payment-method-info {
          font-size: 0.75rem;
          color: #5A5A5A;
          margin-top: 0.15rem;
          display: flex;
          flex-direction: column;
          gap: 0.1rem;
        }

        .card-name {
          font-size: 0.65rem;
          color: #9ca3af;
        }

        .payment-method-actions {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-top: 0.25rem;
        }

        .set-default-btn {
          font-size: 0.6rem;
          background: none;
          border: none;
          color: #3b82f6;
          cursor: pointer;
          padding: 0.1rem 0.3rem;
          border-radius: 4px;
          transition: all 0.3s ease;
        }

        .set-default-btn:hover {
          background: #dbeafe;
          text-decoration: underline;
        }

        .remove-btn {
          background: none;
          border: none;
          color: #dc2626;
          cursor: pointer;
          padding: 0.2rem 0.4rem;
          border-radius: 4px;
          transition: all 0.3s ease;
          font-size: 0.8rem;
        }

        .remove-btn:hover {
          background: #fee2e2;
        }

        /* Modal */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 1rem;
        }

        .modal-content {
          background: #ffffff;
          border-radius: 16px;
          max-width: 500px;
          width: 100%;
          max-height: 95vh;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(0,0,0,0.2);
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.25rem 1.5rem;
          border-bottom: 1px solid #e5e5e5;
        }

        .modal-header h2 {
          color: #006B2E;
          font-size: 1.1rem;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .modal-header h2 svg {
          color: #D9A321;
        }

        .close-btn {
          background: none;
          border: none;
          font-size: 1.25rem;
          cursor: pointer;
          color: #5A5A5A;
          padding: 0.25rem;
          border-radius: 50%;
          transition: all 0.3s ease;
        }

        .close-btn:hover {
          background: #f0f0f0;
        }

        .payment-form {
          padding: 1.5rem;
          overflow-y: auto;
        }

        .payment-type-selector {
          display: flex;
          gap: 0.5rem;
        }

        .type-btn {
          flex: 1;
          padding: 0.5rem;
          border: 2px solid #e5e5e5;
          border-radius: 8px;
          background: #ffffff;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 0.813rem;
          font-weight: 500;
          color: #5A5A5A;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }

        .type-btn:hover {
          border-color: #D9A321;
        }

        .type-btn.active {
          border-color: #D9A321;
          background: #fef3c7;
          color: #006B2E;
        }

        .form-group {
          margin-bottom: 1rem;
        }

        .form-group label {
          display: block;
          font-weight: 500;
          font-size: 0.813rem;
          color: #1F1F1F;
          margin-bottom: 0.25rem;
        }

        .form-group label .helper-text {
          display: block;
          font-weight: 400;
          font-size: 0.65rem;
          color: #9ca3af;
          margin-top: 0.1rem;
        }

        .form-group input {
          width: 100%;
          padding: 0.5rem 0.75rem;
          border: 1px solid #e5e5e5;
          border-radius: 8px;
          font-size: 0.875rem;
          transition: all 0.3s ease;
          background: #ffffff;
          color: #1F1F1F;
        }

        .form-group input:focus {
          outline: none;
          border-color: #D9A321;
          box-shadow: 0 0 0 3px rgba(217,163,33,0.15);
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.75rem;
        }

        .checkbox-group {
          margin: 0.5rem 0;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.813rem;
          color: #5A5A5A;
          cursor: pointer;
        }

        .checkbox-label input[type="checkbox"] {
          width: 18px;
          height: 18px;
          accent-color: #D9A321;
          cursor: pointer;
        }

        .form-error {
          background: #fee2e2;
          color: #dc2626;
          padding: 0.5rem 0.75rem;
          border-radius: 8px;
          font-size: 0.813rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 0.75rem;
        }

        .form-actions {
          display: flex;
          gap: 0.75rem;
          justify-content: flex-end;
          margin-top: 1rem;
          padding-top: 1rem;
          border-top: 1px solid #e5e5e5;
        }

        .form-actions button {
          padding: 0.5rem 1.25rem;
          border-radius: 8px;
          font-size: 0.875rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          border: none;
        }

        .form-actions .btn-secondary {
          background: #f0f0f0;
          color: #1F1F1F;
        }

        .form-actions .btn-secondary:hover {
          background: #e5e5e5;
        }

        .form-actions .btn-primary {
          background: #D9A321;
          color: #ffffff;
        }

        .form-actions .btn-primary:hover:not(:disabled) {
          background: #B8860B;
        }

        .form-actions .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        @media (max-width: 480px) {
          .payment-methods-grid {
            grid-template-columns: 1fr;
          }
          
          .modal-content {
            max-width: 100%;
            margin: 0.5rem;
          }

          .form-row {
            grid-template-columns: 1fr;
          }

          .payment-type-selector {
            flex-direction: column;
          }

          .form-actions {
            flex-direction: column;
          }

          .form-actions button {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default PaymentMethods;
